// lib/i18n.tsx — sistema de internacionalización ligero (sin dependencias externas)
import React, { createContext, useContext, useState, useCallback } from 'react';
import { es } from '../locales/es';
import { en } from '../locales/en';
import type { Translations } from '../locales/es';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export type Lang = 'es' | 'en';

const LOCALES: Record<Lang, Translations> = { es, en };
const SUPPORTED: Lang[] = ['es', 'en'];
const STORAGE_KEY = 'grindlog_lang';

// ─── Detección del idioma ─────────────────────────────────────────────────────

function detectLang(): Lang {
  const saved = localStorage.getItem(STORAGE_KEY) as Lang | null;
  if (saved && SUPPORTED.includes(saved)) return saved;
  return 'es'; // default siempre español hasta que el usuario elija explícitamente
}

// ─── Interpolación: t('goals.labelBankroll', { val: '100' }) ─────────────────

export function interpolate(str: string, vars?: Record<string, string | number>): string {
  if (!vars) return str;
  return Object.entries(vars).reduce(
    (s, [k, v]) => s.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), String(v)),
    str
  );
}

// ─── Context ──────────────────────────────────────────────────────────────────

interface I18nCtx {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nCtx>({
  lang: 'es',
  setLang: () => { },
  t: (key) => key,
});

// ─── Provider ─────────────────────────────────────────────────────────────────

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectLang);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    localStorage.setItem(STORAGE_KEY, l);
    document.documentElement.setAttribute('lang', l);
  }, []);

  // Resolver key anidada: "nav.dashboard" → locales[lang].nav.dashboard
  const t = useCallback((key: string, vars?: Record<string, string | number>): string => {
    const parts = key.split('.');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let value: any = LOCALES[lang];
    for (const part of parts) {
      value = value?.[part];
      if (value === undefined) break;
    }
    // Fallback a español si la clave no existe en el idioma actual
    if (typeof value !== 'string') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let fallback: any = LOCALES['es'];
      for (const part of parts) {
        fallback = fallback?.[part];
        if (fallback === undefined) break;
      }
      value = typeof fallback === 'string' ? fallback : key;
    }
    return interpolate(value, vars);
  }, [lang]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useI18n() {
  return useContext(I18nContext);
}

// Shorthand: const { t } = useT()
export function useT() {
  const { t, lang, setLang } = useContext(I18nContext);
  return { t, lang, setLang };
}

// ─── Language toggle button (reutilizable) ────────────────────────────────────

export function LangToggle({ className }: { className?: string }) {
  const { lang, setLang } = useT();
  return (
    <button
      className={className ?? 'lang-toggle'}
      onClick={() => setLang(lang === 'es' ? 'en' : 'es')}
      title={lang === 'es' ? 'Switch to English' : 'Cambiar a Español'}
      aria-label="Toggle language"
    >
      {lang === 'es' ? '🇺🇸 EN' : '🇦🇷 ES'}
    </button>
  );
}
